import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import axios from "../utils/axios.tsx";
import { useTypedSelector } from "../hooks.tsx";
import {
  requestSpeech,
  useTextToSpeech,
  type SpeechRequest,
} from "./useTextToSpeech.ts";
import { searchFieldActions } from "../store/search/searchField.tsx";
import { getIsConversationWorking } from "../store/conversation.tsx";
import { stopStream } from "../components/search/streamAsk.tsx";
import { setPlaybackDucked } from "../voice/audioPlayback.ts";
import { VoiceCapture } from "../voice/voiceCapture.ts";
import {
  newNarrationState,
  observeProgress,
  recordFill,
  recordNarration,
  shouldFill,
  shouldNarrate,
} from "../voice/narration.ts";
import { loadClip, pickPhrase, preloadClips } from "../voice/voiceClips.ts";
import { newAnswerSpeech, takeSpeakable } from "../voice/answerStream.ts";
import {
  isVisualResponseType,
  toSpeakableAnswer,
} from "../voice/speakableText.ts";
import { isUsableTranscript } from "../voice/transcript.ts";
import {
  initialVoiceSessionContext,
  voiceSessionReducer,
  type VoiceSessionContext,
} from "../voice/voiceSessionMachine.ts";

/**
 * How long we wait for the ask to actually start after submitting. If the
 * request never gets off the ground, the selected conversation's "working"
 * state never flips and the session would otherwise sit in "thinking" forever.
 */
const ANSWER_START_TIMEOUT_MS = 20_000;
/** Upper bound on one answer. The p90 question uses ~36s of LLM time. */
const ANSWER_TIMEOUT_MS = 180_000;
/**
 * Once Tally is reading an answer that is still streaming, how long it waits
 * for the next sentence before treating the answer as done. Without it, a
 * stream that stalls after its first sentence holds the session (and the
 * microphone) in "speaking" indefinitely.
 */
const ANSWER_STALL_MS = 30_000;
/** How often the wait-narration pacing is re-evaluated while thinking. */
const NARRATION_POLL_MS = 1_000;
/** Playback watchdog slack, on top of a rough per-character speech estimate. */
const PLAYBACK_WATCHDOG_BASE_MS = 15_000;
const PLAYBACK_WATCHDOG_PER_CHAR_MS = 100;
/**
 * Longest a wait phrase may hold the floor, fetch included, before the answer
 * stops waiting for it. Phrases are a few words; a hung one must not stall.
 */
const PHRASE_WATCHDOG_BASE_MS = 5_000;

/** A piece of the answer waiting its turn, with its audio once requested. */
interface QueuedSpeech {
  text: string;
  request?: SpeechRequest;
}

export interface VoiceSessionCopy {
  /** Spoken in place of a chart or table, which have no prose of their own. */
  visualFallback: string;
  /** Appended when a long answer is cut short for speech. */
  truncationSuffix: string;
  /** Said as soon as a question is heard, one picked at random. */
  acknowledgements: string[];
  /** Said when a step runs long with nothing new to narrate. */
  fillers: string[];
}

export interface UseVoiceSessionOptions {
  /** Whether the session should be running. */
  active: boolean;
  /** Called when the session stops for any reason, including its own decision. */
  onEnded: () => void;
  copy: VoiceSessionCopy;
}

export interface UseVoiceSessionResult {
  context: VoiceSessionContext;
  /** Current microphone level, 0..1, for the overlay animation. */
  level: number;
  /** Stop playback and go straight back to listening. */
  interrupt: () => void;
}

export function useVoiceSession({
  active,
  onEnded,
  copy,
}: UseVoiceSessionOptions): UseVoiceSessionResult {
  const [context, dispatchEvent] = useReducer(
    voiceSessionReducer,
    initialVoiceSessionContext,
  );
  const [level, setLevel] = useState(0);

  const dispatch = useDispatch();
  const { speak, playClip, stop } = useTextToSpeech();
  // `speak`'s identity changes as the speech hook's own state advances
  // (idle -> loading -> playing). Depending on it directly would re-run the
  // speaking effect mid-answer, stopping and restarting playback on a loop.
  const speakRef = useRef(speak);
  speakRef.current = speak;
  const playClipRef = useRef(playClip);
  playClipRef.current = playClip;

  const captureRef = useRef<VoiceCapture | null>(null);
  const playbackWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  /** Latest values for callbacks that are installed once, at capture creation. */
  const handlersRef = useRef({
    onUtterance: (_audio: Blob) => {},
    onNoSpeech: () => {},
    onBargeIn: () => {},
    onSpeechStart: () => {},
    onSpeechEnd: () => {},
    onError: (_error: Error) => {},
  });

  const selectedConversation = useTypedSelector(
    (state) => state.conversationState.selectedConversation,
  );
  // Read from callbacks installed once, so held in a ref like the others.
  const selectedConversationRef = useRef(selectedConversation);
  selectedConversationRef.current = selectedConversation;
  const isSelectedConversationWorking = useTypedSelector((state) =>
    getIsConversationWorking(state, selectedConversation),
  );
  const exchangesByConversation = useTypedSelector(
    (state) => state.conversationState.exchangesByConversation,
  );

  const copyRef = useRef(copy);
  copyRef.current = copy;
  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;

  // --- transcription -------------------------------------------------------

  const transcribe = useCallback(async (audio: Blob) => {
    dispatchEvent({ type: "SPEECH_CAPTURED" });

    const formData = new FormData();
    formData.append("file", audio, "audio.webm");

    try {
      const response = await axios.post("/v1/speech-to-text/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const text: string = response?.data?.text ?? "";
      if (!isUsableTranscript(text)) {
        dispatchEvent({ type: "TRANSCRIPTION_EMPTY" });
        return;
      }
      dispatchEvent({ type: "TRANSCRIBED", text: text.trim() });
    } catch (error) {
      console.error("Voice mode: transcription failed", error);
      dispatchEvent({ type: "TRANSCRIPTION_FAILED" });
    }
  }, []);

  handlersRef.current.onUtterance = (audio: Blob) => {
    void transcribe(audio);
  };
  handlersRef.current.onNoSpeech = () => {
    dispatchEvent({ type: "NO_SPEECH_DETECTED" });
  };
  // Talking over Tally first turns it down, then stops it and listens once the
  // speech has lasted. While Tally speaks the question is kept, as the answer
  // may still be streaming and the written answer is still wanted. While it
  // thinks there is no answer yet, so the question is cancelled, as Stop does:
  // the user is asking something else.
  handlersRef.current.onSpeechStart = () => setPlaybackDucked(true);
  handlersRef.current.onSpeechEnd = () => setPlaybackDucked(false);
  handlersRef.current.onBargeIn = () => {
    const cancelQuestion = stateRef.current === "thinking";
    setPlaybackDucked(false);
    stop();
    dispatchEvent({ type: "BARGE_IN" });
    if (cancelQuestion) stopStream(selectedConversationRef.current);
  };
  handlersRef.current.onError = (error: Error) => {
    console.error("Voice mode: capture error", error);
    dispatchEvent({ type: "MIC_FAILED" });
  };

  // --- session lifecycle ---------------------------------------------------

  useEffect(() => {
    if (!active) return;

    // Start from a pristine context so this session's own end is distinguishable
    // from the previous one's - otherwise a restart that fails immediately
    // leaves `shouldEnd` unchanged and the overlay with no way to close itself.
    dispatchEvent({ type: "SESSION_RESET" });

    let cancelled = false;
    const capture = new VoiceCapture({
      onUtterance: (audio) => handlersRef.current.onUtterance(audio),
      onNoSpeech: () => handlersRef.current.onNoSpeech(),
      onBargeIn: () => handlersRef.current.onBargeIn(),
      onSpeechStart: () => handlersRef.current.onSpeechStart(),
      onSpeechEnd: () => handlersRef.current.onSpeechEnd(),
      onLevel: (value) => setLevel(value),
      onError: (error) => handlersRef.current.onError(error),
    });
    captureRef.current = capture;

    preloadClips([
      ...copyRef.current.acknowledgements,
      ...copyRef.current.fillers,
    ]);

    capture
      .open()
      .then(() => {
        if (cancelled) {
          capture.close();
          return;
        }
        dispatchEvent({ type: "SESSION_STARTED" });
      })
      .catch((error) => {
        console.error("Voice mode: microphone unavailable", error);
        if (!cancelled) dispatchEvent({ type: "MIC_FAILED" });
      });

    return () => {
      cancelled = true;
      capture.close();
      captureRef.current = null;
      setLevel(0);
      setPlaybackDucked(false);
      // Leaving voice mode has to silence Tally too. Closing the overlay while
      // an answer is still being read would otherwise leave a disembodied voice
      // talking over whatever the user does next.
      stop();
      // And the machine has to stop with it. Left in "thinking", the narration
      // timer kept talking and the answer was still read out when it arrived.
      // A reset rather than SESSION_ENDED: the host has already ended voice
      // mode, so there is nobody to tell.
      dispatchEvent({ type: "SESSION_RESET" });
    };
  }, [active, stop]);

  // The machine decided to stop (idle rounds exhausted, mic failure, or an
  // explicit end). Tell the host so it can clear the request flag.
  useEffect(() => {
    if (!context.shouldEnd) return;
    stop();
    onEndedRef.current();
  }, [context.shouldEnd, stop]);

  // Holding the microphone open on a tab nobody is looking at is not something
  // to do quietly, and background tabs throttle the timers the VAD relies on.
  useEffect(() => {
    if (!active) return;
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        dispatchEvent({ type: "SESSION_ENDED" });
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [active]);

  // --- listening -----------------------------------------------------------

  useEffect(() => {
    if (context.state !== "listening") return;
    setPlaybackDucked(false);
    captureRef.current?.arm();
  }, [context.state, context.idleRounds]);

  // --- asking --------------------------------------------------------------

  // Guards against submitting the same turn twice when the effect re-runs.
  // Deliberately a flag reset on leaving "thinking", not a comparison against
  // the last transcript: asking the same question twice in a row is normal,
  // and comparing the text would silently drop the second one.
  const submittedThisTurnRef = useRef(false);

  useEffect(() => {
    if (context.state !== "thinking") {
      submittedThisTurnRef.current = false;
      return;
    }
    if (submittedThisTurnRef.current) return;

    submittedThisTurnRef.current = true;
    dispatch(
      searchFieldActions.setTextAndSubmitImmediately(context.transcript),
    );
  }, [context.state, context.transcript, dispatch]);

  // --- speech queue ---------------------------------------------------------

  // The answer is spoken piece by piece: `pumpSpeech` plays the next queued
  // piece when the previous one ends, and finishes the turn once the queue is
  // empty and the answer has been fully handed over.
  //
  // The piece at the head of the queue has its audio requested as soon as it
  // is there, while a wait phrase or the previous piece is still playing, so
  // the speech service's first-byte delay is not heard as a pause.
  const answerSpeechRef = useRef(newAnswerSpeech());
  const speechQueueRef = useRef<QueuedSpeech[]>([]);
  const speechPlayingRef = useRef(false);
  const speechTurnRef = useRef(0);
  /** The last exchange when this turn began, so its answer is not re-read. */
  const turnStartExchangeRef = useRef<string | undefined>(undefined);
  const stateRef = useRef(context.state);
  stateRef.current = context.state;
  /**
   * True while a wait phrase (acknowledgement, narration, filler) is being
   * fetched or played. Nothing else starts until it ends - not the next
   * phrase, and not the answer - so Tally is never cut off mid-word.
   */
  const phraseBusyRef = useRef(false);
  /**
   * Whether the wait phrase has started playing. Until it has, it is still
   * being fetched and nobody has heard a word of it - so an answer that is
   * ready by then goes first and the phrase is dropped.
   */
  const phraseAudibleRef = useRef(false);
  const phraseSeqRef = useRef(0);
  const stallTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearStallTimer = () => {
    if (stallTimerRef.current !== null) {
      clearTimeout(stallTimerRef.current);
      stallTimerRef.current = null;
    }
  };

  const prefetchNextPiece = () => {
    const head = speechQueueRef.current[0];
    if (head && !head.request) {
      head.request = requestSpeech(exchangeIdRef.current, head.text);
    }
  };
  /** Drops whatever is queued, cancelling audio already requested for it. */
  const clearSpeechQueue = () => {
    speechQueueRef.current.forEach((piece) =>
      piece.request?.controller.abort(),
    );
    speechQueueRef.current = [];
  };

  const pumpSpeech = useCallback(() => {
    if (
      stateRef.current === "speaking" &&
      phraseBusyRef.current &&
      !phraseAudibleRef.current &&
      speechQueueRef.current.length > 0
    ) {
      // Ending its turn makes a late end or watchdog from it a no-op.
      phraseSeqRef.current++;
      phraseBusyRef.current = false;
      stop();
    }
    if (
      stateRef.current !== "speaking" ||
      speechPlayingRef.current ||
      phraseBusyRef.current
    ) {
      return;
    }

    const next = speechQueueRef.current.shift();
    if (next === undefined) {
      if (answerSpeechRef.current.finished) {
        dispatchEvent({ type: "PLAYBACK_FINISHED" });
      } else if (stallTimerRef.current === null) {
        // Waiting on the stream for the next sentence - but not forever.
        stallTimerRef.current = setTimeout(() => {
          stallTimerRef.current = null;
          answerSpeechRef.current = {
            ...answerSpeechRef.current,
            finished: true,
          };
          pumpSpeech();
        }, ANSWER_STALL_MS);
      }
      return;
    }
    clearStallTimer();
    prefetchNextPiece();

    speechPlayingRef.current = true;
    const turn = speechTurnRef.current;
    let settled = false;
    const done = (failed: boolean) => {
      if (settled || turn !== speechTurnRef.current) return;
      settled = true;
      if (playbackWatchdogRef.current !== null) {
        clearTimeout(playbackWatchdogRef.current);
        playbackWatchdogRef.current = null;
      }
      speechPlayingRef.current = false;
      if (failed) {
        dispatchEvent({ type: "PLAYBACK_FAILED" });
      } else {
        pumpSpeech();
      }
    };

    playbackWatchdogRef.current = setTimeout(
      () => {
        // Otherwise the only trace of a stalled answer is the notice.
        console.warn("Voice mode: answer audio never finished, giving up");
        done(true);
      },
      PLAYBACK_WATCHDOG_BASE_MS +
        next.text.length * PLAYBACK_WATCHDOG_PER_CHAR_MS,
    );

    // `speak` settles when the audio finishes downloading, which is earlier
    // than the end of playback - only `onEnded` means Tally has stopped
    // talking. The promise is still awaited so a failed request is not silent.
    speakRef
      .current(exchangeIdRef.current, next.text, {
        onEnded: () => done(false),
        request: next.request,
      })
      .catch((error) => {
        console.error("Voice mode: playback failed", error);
        done(true);
      });
  }, [stop]);

  // --- narrating the wait ---------------------------------------------------

  // The assistant tree emits a progress update as it moves between assistants,
  // and the store keeps the newest one on the pending exchange. Saying the slow
  // steps' spoken lines is what turns a 10-36 second silence into something
  // that sounds like work being done.
  const lastExchange = (() => {
    const exchanges = exchangesByConversation[selectedConversation];
    const lastId = exchanges?.allIds[exchanges.allIds.length - 1];
    return lastId ? exchanges.byId[lastId] : undefined;
  })();

  // Only the spoken version is said. The on-screen line is a status label
  // ("Looking up the numbers"), which sounds robotic read aloud, and a step
  // without a spoken version is one not worth talking over.
  const progressText = lastExchange?.progress_update
    ? (lastExchange.spoken_progress ?? "")
    : "";

  // Held in refs so the pacing timer below does not restart on every update.
  const progressTextRef = useRef(progressText);
  progressTextRef.current = progressText;
  // Passed to the speech endpoint so spoken audio can be attributed to the
  // exchange it belongs to. It is optional there - narration often runs before
  // an exchange exists - but send it whenever we have one.
  const exchangeIdRef = useRef<string | undefined>(undefined);
  exchangeIdRef.current = lastExchange?.id;
  const narrationRef = useRef(newNarrationState(0));
  const lastPhraseRef = useRef<string | null>(null);

  useEffect(() => {
    if (context.state !== "thinking") return;

    narrationRef.current = newNarrationState(Date.now());
    answerSpeechRef.current = newAnswerSpeech();
    clearSpeechQueue();
    turnStartExchangeRef.current = exchangeIdRef.current;

    let cancelled = false;
    phraseBusyRef.current = false;

    // Listen for the user talking over the wait, as during the answer - once
    // the acknowledgement is over. Talking over the wait cancels the question,
    // and until the microphone has heard Tally's voice it could take Tally for
    // the user. Idle meanwhile, it learns that voice from the acknowledgement.
    const watch = () => {
      if (!cancelled) captureRef.current?.watchForBargeIn();
    };

    // Runs one wait phrase, holding the busy flag until it has finished. A
    // timeout releases it too: a phrase that never reports its end must not
    // hold back the answer.
    const runPhrase = (
      text: string,
      play: (onEnded: () => void, onStarted: () => void) => Promise<void>,
      onDone?: () => void,
    ) => {
      phraseBusyRef.current = true;
      phraseAudibleRef.current = false;
      const seq = ++phraseSeqRef.current;
      const started = () => {
        if (seq === phraseSeqRef.current) phraseAudibleRef.current = true;
      };
      const release = () => {
        if (seq !== phraseSeqRef.current) return;
        clearTimeout(timeout);
        phraseBusyRef.current = false;
        onDone?.();
        // The answer may have been waiting for this phrase to finish.
        pumpSpeech();
      };
      const timeout = setTimeout(
        release,
        PHRASE_WATCHDOG_BASE_MS + text.length * PLAYBACK_WATCHDOG_PER_CHAR_MS,
      );
      play(release, started).catch(release);
    };

    const playPhrase = (pool: string[], onDone?: () => void) => {
      if (pool.length === 0) return onDone?.();
      const text = pickPhrase(pool, lastPhraseRef.current);
      lastPhraseRef.current = text;
      runPhrase(
        text,
        async (onEnded, onStarted) => {
          const clip = await loadClip(text);
          // Loaded after the turn moved on: say nothing, just let go.
          if (cancelled) return onEnded();
          await playClipRef.current(clip, { onEnded, onStarted });
        },
        onDone,
      );
    };

    playPhrase(copyRef.current.acknowledgements, watch);

    // Polled rather than driven off progress changes: the first update usually
    // arrives before the initial delay is up, so there would be no later change
    // to react to.
    const timer = setInterval(() => {
      const now = Date.now();
      narrationRef.current = observeProgress(
        narrationRef.current,
        progressTextRef.current,
      );
      // Never talk over Tally: wait, and say what is current once it is free.
      if (phraseBusyRef.current) return;

      if (!shouldNarrate(narrationRef.current, now)) {
        if (shouldFill(narrationRef.current, now)) {
          narrationRef.current = recordFill(narrationRef.current, now);
          playPhrase(copyRef.current.fillers);
        }
        return;
      }

      const text = narrationRef.current.observedText;
      narrationRef.current = recordNarration(narrationRef.current, now);
      // A failed progress line just releases the flag; the answer still comes.
      runPhrase(text, (onEnded, onStarted) =>
        speakRef.current(exchangeIdRef.current, text, { onEnded, onStarted }),
      );
    }, NARRATION_POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [context.state, context.transcript]);

  // Hand the answer to the speech queue. A text answer is cut into sentences as
  // it streams, so Tally starts talking after the first one; anything else is
  // spoken whole once the ask finishes. The selected conversation's "working"
  // state flips true when the ask starts and false when it finishes, so we wait
  // for that full round trip rather than assuming it is already true when we
  // arrive here.
  const sawAwaitingRef = useRef(false);

  useEffect(() => {
    if (context.state !== "thinking" && context.state !== "speaking") {
      sawAwaitingRef.current = false;
      return;
    }
    if (isSelectedConversationWorking) sawAwaitingRef.current = true;
    if (!sawAwaitingRef.current) return;

    const final = !isSelectedConversationWorking;
    const exchanges = exchangesByConversation[selectedConversation];
    const lastId = exchanges?.allIds[exchanges.allIds.length - 1];
    const lastExchange = lastId ? exchanges.byId[lastId] : undefined;

    if (final && !lastExchange) {
      dispatchEvent({ type: "ANSWER_FAILED" });
      return;
    }

    const streamingText =
      lastExchange !== undefined &&
      typeof lastExchange.response === "string" &&
      !isVisualResponseType(lastExchange.type);

    if (!final) {
      // Mid-stream, only a text answer for this turn - not the previous
      // turn's, still last until the new exchange is appended - has anything
      // to say.
      if (
        !streamingText ||
        lastExchange.progress_update ||
        lastExchange.id === turnStartExchangeRef.current
      ) {
        return;
      }
    }

    if (streamingText) {
      const { text, state } = takeSpeakable(
        lastExchange.response as string,
        answerSpeechRef.current,
        final,
        copyRef.current.truncationSuffix,
      );
      answerSpeechRef.current = state;
      if (text !== "") speechQueueRef.current.push({ text });
    } else if (context.state === "thinking") {
      const { text } = toSpeakableAnswer(
        lastExchange!.response,
        lastExchange!.type,
        {
          visualFallback: copyRef.current.visualFallback,
          truncationSuffix: copyRef.current.truncationSuffix,
        },
      );
      answerSpeechRef.current = { ...answerSpeechRef.current, finished: true };
      if (text !== "") speechQueueRef.current.push({ text });
    }
    prefetchNextPiece();

    if (context.state === "speaking") {
      pumpSpeech();
    } else if (speechQueueRef.current.length > 0) {
      dispatchEvent({
        type: "ANSWER_RECEIVED",
        text: speechQueueRef.current[0].text,
      });
    } else if (final) {
      dispatchEvent({ type: "ANSWER_FAILED" });
    }
  }, [
    context.state,
    isSelectedConversationWorking,
    exchangesByConversation,
    selectedConversation,
    pumpSpeech,
  ]);

  // Watchdogs for the ask, so a request that never starts or never finishes
  // does not strand the session.
  useEffect(() => {
    if (context.state !== "thinking") return;

    const startTimer = setTimeout(() => {
      if (!sawAwaitingRef.current) dispatchEvent({ type: "ANSWER_FAILED" });
    }, ANSWER_START_TIMEOUT_MS);
    const answerTimer = setTimeout(() => {
      dispatchEvent({ type: "ANSWER_FAILED" });
    }, ANSWER_TIMEOUT_MS);

    return () => {
      clearTimeout(startTimer);
      clearTimeout(answerTimer);
    };
  }, [context.state, context.transcript]);

  // --- speaking ------------------------------------------------------------

  useEffect(() => {
    if (context.state !== "speaking") return;

    // Listen for the user talking over the answer.
    captureRef.current?.watchForBargeIn();
    pumpSpeech();

    return () => {
      // Leaving "speaking" abandons whatever is queued; a late `onEnded` or
      // watchdog from this turn must not drive the next one.
      speechTurnRef.current++;
      phraseSeqRef.current++;
      phraseBusyRef.current = false;
      speechPlayingRef.current = false;
      clearSpeechQueue();
      clearStallTimer();
      if (playbackWatchdogRef.current !== null) {
        clearTimeout(playbackWatchdogRef.current);
        playbackWatchdogRef.current = null;
      }
    };
  }, [context.state, pumpSpeech]);

  // An explicit Stop. While Tally thinks there is nothing to silence, so it
  // cancels the question. While it speaks it only silences the voice: the
  // answer may still be streaming, and the written answer is still wanted.
  const interrupt = useCallback(() => {
    const cancelQuestion = stateRef.current === "thinking";
    stop();
    dispatchEvent({ type: "INTERRUPTED" });
    if (cancelQuestion) stopStream(selectedConversationRef.current);
  }, [stop]);

  return { context, level, interrupt };
}
